import type { ConnectorRegistryRow } from "../../components/connectors";
import type { ConnectorInstanceSummary } from "../../components/connectors/ConnectorCard.types";
import {
  CONNECTOR_CATEGORY_BY_BACKEND_CATEGORY,
  CONNECTOR_CATEGORY_BY_KEY,
} from "../../data/connectorCategories";
import { connectors } from "../../data/connectors";
import {
  comingSoonConnectors,
  type ComingSoonConnector,
} from "../../data/comingSoonConnectors";
import {
  normalizeConnectorKey,
  resolveConnectorIconUrl,
} from "../../utils/connectorIcons";
import type {
  BackendConnectorInstance,
  BackendConnectorRegistryEntry,
} from "../api";

export const CATEGORY_LABELS: Record<string, string> = {
  ANALYTICS: "Analytics",
  APP: "Apps",
  BI: "Business Intelligence",
  CRM: "CRM",
  DATABASE: "Databases",
  DATA_WAREHOUSE: "Data Warehouse",
  FILES: "Files",
  PRODUCTIVITY: "Productivity",
};

const AUTH_TYPE_BY_CATALOG_KEY: Partial<Record<string, string>> = {
  airtable: "OAuth",
  notion: "OAuth",
  excel: "File upload",
  qlik: "OAuth",
  salesforce: "OAuth",
  tableau: "Personal access token",
};

const CONNECTOR_ALIASES: Record<string, string> = {
  bigquery: "googlebigquery",
  // NOTE: `googlesheets` used to alias to "excel", back when Sheets had no
  // entry of its own and borrowed the Excel connector's branding. It now has
  // a real entry in data/connectors.ts, so the alias made every Sheets
  // surface (connectors list, connection detail, instance sidebar) render
  // Microsoft Excel's green "X" logo and branding instead of its own —
  // confirmed live 2026-08-30. Do not re-add it.
};

const SUCCESS_INSTANCE_STATUSES = new Set([
  "connected",
  "healthy",
  "success",
  "ok",
]);
const DISCONNECTED_INSTANCE_STATUSES = new Set([
  "disconnected",
  "failed",
  "error",
  "inactive",
]);

const toCatalogKey = (
  integrationUrl: string | undefined,
  name: string,
): string => {
  if (integrationUrl) {
    const segments = integrationUrl.split("/").filter(Boolean);
    const tail = segments[segments.length - 1];
    if (tail) return tail.toLowerCase();
  }
  return name.toLowerCase().replace(/\s+/g, "");
};

const prettifyConnectorName = (key: string): string =>
  key.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const toCatalogKeyFromDisplayName = (value: string): string =>
  value.toLowerCase().replace(/[\s_-]+/g, "");

const LOCAL_CONNECTOR_BY_KEY = (() => {
  const out = new Map<string, (typeof connectors)[number]>();
  for (const connector of connectors) {
    const candidates = [
      connector.name,
      connector.header,
      toCatalogKey(connector.intergration?.url, connector.name),
    ];
    for (const candidate of candidates) {
      out.set(normalizeConnectorKey(candidate), connector);
    }
  }
  return out;
})();

/** Tab for a connector no category covers. */
export const OTHER_CATEGORY_LABEL = "Other";

/**
 * Category key for a connector: the explicit per-key map first (it holds
 * finer groups such as Databases), then the backend's catalog category, then
 * the local catalog entry.
 */
const resolveConnectorCategoryType = (
  catalogKey: string,
  backendCategory?: string | null,
  fallbackType?: string,
): string | undefined =>
  CONNECTOR_CATEGORY_BY_KEY[normalizeConnectorKey(catalogKey)] ??
  (backendCategory
    ? CONNECTOR_CATEGORY_BY_BACKEND_CATEGORY[backendCategory.toLowerCase()]
    : undefined) ??
  fallbackType;

const resolveLocalConnectorMeta = (catalogKey: string) => {
  const normalized = normalizeConnectorKey(catalogKey);
  const alias = CONNECTOR_ALIASES[normalized];
  if (alias && LOCAL_CONNECTOR_BY_KEY.has(alias)) {
    return LOCAL_CONNECTOR_BY_KEY.get(alias);
  }
  return LOCAL_CONNECTOR_BY_KEY.get(normalized);
};

export interface ConnectorBranding {
  /** Normalised catalog key the branding was resolved from (e.g. "tableau"). */
  catalogKey: string;
  /** Human label — local catalog name, else the prettified key. */
  displayName: string;
  /** Logo path — local catalog logo, else a shipped brand asset, else a generic category icon. */
  iconUrl: string;
}

/**
 * Resolve display name + logo for a bare connector key.
 *
 * Endpoints that return only a `connector_type` string (story data sources,
 * asset rows) have no registry entry to map from — this exposes the same
 * local-catalog lookup `mapBackendConnectorRegistryEntry` uses so those
 * callers render identical branding instead of re-deriving it.
 */
export const resolveConnectorBranding = (
  connectorType: string,
): ConnectorBranding => {
  const catalogKey = connectorType.trim() || "connector";
  const localMeta = resolveLocalConnectorMeta(catalogKey);
  return {
    catalogKey,
    displayName: localMeta?.name ?? prettifyConnectorName(catalogKey),
    iconUrl: resolveConnectorIconUrl({
      catalogKey,
      categoryType: localMeta?.type,
      localLogo: localMeta?.logo,
    }),
  };
};

const getInstanceStatus = (
  instance: BackendConnectorInstance,
): string | null => {
  const value = instance.status ?? instance.connection_status ?? null;
  if (typeof value !== "string") return null;
  const normalized = value.toLowerCase().trim();
  return normalized.length > 0 ? normalized : null;
};

const mapBackendStatusToCardStatus = (
  status: string,
  hasConnection: boolean,
): ConnectorRegistryRow["status"] => {
  const normalized = status.toLowerCase();
  if (normalized === "connected") return "connected";
  if (normalized === "error") return "error";
  if (normalized === "disconnected" && !hasConnection) return "not_connected";
  if (normalized === "disconnected") return "disconnected";
  return hasConnection ? "connected" : "not_connected";
};

const deriveCardStatusFromInstances = (
  entry: BackendConnectorRegistryEntry,
  instances: BackendConnectorInstance[],
  hasConnection: boolean,
): ConnectorRegistryRow["status"] => {
  const resolvedStatuses = instances
    .map(getInstanceStatus)
    .filter((value): value is string => value !== null);

  if (
    resolvedStatuses.some((status) => SUCCESS_INSTANCE_STATUSES.has(status))
  ) {
    return "connected";
  }

  if (
    resolvedStatuses.length === instances.length &&
    resolvedStatuses.length > 0 &&
    resolvedStatuses.every((status) =>
      DISCONNECTED_INSTANCE_STATUSES.has(status),
    )
  ) {
    return hasConnection ? "disconnected" : "not_connected";
  }

  if (instances.length > 0) {
    // If instance-level statuses are not present, treat presence of instances as connected.
    return "connected";
  }

  return mapBackendStatusToCardStatus(entry.status ?? "", hasConnection);
};

const mapInstance = (
  instance: BackendConnectorInstance,
): ConnectorInstanceSummary => ({
  // Route params expect connector connection IDs (not internal instance IDs).
  connectorId:
    instance.connector_id ??
    (instance as { connection_id?: string }).connection_id ??
    (instance as { id?: string | number }).id?.toString() ??
    "",
  connectionName: instance.connection_name ?? "Untitled connection",
  lastSync:
    instance.last_sync ??
    (instance as { created_at?: string }).created_at ??
    undefined,
  status: getInstanceStatus(instance) ?? undefined,
});

export const mapBackendConnectorRegistryEntry = (
  entry: BackendConnectorRegistryEntry,
): ConnectorRegistryRow => {
  const catalogKeyRaw = entry.key ?? entry.connector ?? "";
  const displayNameRaw = entry.display_name ?? "";
  const catalogKey =
    catalogKeyRaw.trim().length > 0
      ? catalogKeyRaw
      : toCatalogKeyFromDisplayName(displayNameRaw || "unknown");

  const localMeta = resolveLocalConnectorMeta(catalogKey);
  const categoryType = resolveConnectorCategoryType(
    catalogKey,
    entry.category,
    localMeta?.type,
  );
  const displayName =
    displayNameRaw.trim().length > 0
      ? displayNameRaw
      : prettifyConnectorName(catalogKey);
  const iconUrl = resolveConnectorIconUrl({
    catalogKey: catalogKey || "connector",
    categoryType,
    localLogo: localMeta?.logo,
    serverIconUrl: entry.icon_url,
  });
  const providerLabel = entry.domain?.trim().length ? entry.domain : undefined;
  const category = categoryType
    ? (CATEGORY_LABELS[categoryType] ?? categoryType)
    : OTHER_CATEGORY_LABEL;

  const mappedInstances = Array.isArray(entry.instances)
    ? entry.instances.map(mapInstance)
    : [];
  const primaryInstance = mappedInstances.find(
    (instance) => instance.connectorId.trim().length > 0,
  );

  const connectionId =
    primaryInstance?.connectorId ??
    entry.connection?.id ??
    entry.connection?.connection_id ??
    null;

  const status = deriveCardStatusFromInstances(
    entry,
    Array.isArray(entry.instances) ? entry.instances : [],
    connectionId !== null,
  );

  return {
    catalogKey,
    displayName,
    providerLabel,
    iconUrl,
    category,
    description:
      entry.description ??
      `Connect ${displayName} to synchronize your data.`,
    status,
    connectionId,
    instanceCount: mappedInstances.length,
    instances: mappedInstances,
    primaryInstance: primaryInstance ?? null,
    lastSyncAt: primaryInstance?.lastSync,
    authType:
      status === "not_connected"
        ? AUTH_TYPE_BY_CATALOG_KEY[catalogKey]
        : undefined,
  };
};

export const mapBackendConnectorRegistry = (
  entries: BackendConnectorRegistryEntry[],
): ConnectorRegistryRow[] => entries.map(mapBackendConnectorRegistryEntry);

const mapComingSoonConnector = (
  connector: ComingSoonConnector,
): ConnectorRegistryRow => ({
  catalogKey: connector.key,
  displayName: connector.name,
  providerLabel: connector.domain,
  iconUrl: resolveConnectorIconUrl({
    catalogKey: connector.logoKey ?? connector.key,
    categoryType: connector.type,
  }),
  category: CATEGORY_LABELS[connector.type] ?? connector.type,
  description: connector.description,
  status: "not_connected",
  connectionId: null,
  comingSoon: true,
});

/**
 * Cards for planned sources, minus any the server already lists (matched on
 * key or name, ignoring case and separators) so a source never shows twice
 * once it ships.
 */
export const mapComingSoonConnectors = (
  liveRows: ConnectorRegistryRow[],
  planned: readonly ComingSoonConnector[] = comingSoonConnectors,
): ConnectorRegistryRow[] => {
  const liveIdentities = new Set(
    liveRows.flatMap((row) => [
      normalizeConnectorKey(row.catalogKey),
      normalizeConnectorKey(row.displayName),
    ]),
  );
  return planned
    .filter(
      (connector) =>
        !liveIdentities.has(normalizeConnectorKey(connector.key)) &&
        !liveIdentities.has(normalizeConnectorKey(connector.name)),
    )
    .map(mapComingSoonConnector);
};
